/**
 * BlockedList — surfaces blocked users and the deliberate two-step unblock
 * (Phase B, AC-11.1). This is the home for `UnblockConfirm`.
 *
 * Blocked users are NOT in the graph's connections list (a block severs the
 * edge), so the source of record here is `safetyService.getBlockedUsers()` —
 * the same authoritative block store the graph reads. Gated by `social.graph`.
 */

import { useCallback, useEffect, useState } from 'react';
import { getBlockedUsers, type BlockedUser } from '../../services/api/safetyService';
import { UnblockConfirm } from './UnblockConfirm';
import './BlockedList.css';

export function BlockedList() {
  const [blocked, setBlocked] = useState<BlockedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState<BlockedUser | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBlocked(await getBlockedUsers());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && blocked.length === 0) {
    return <p className="blocked-list__empty">Loading…</p>;
  }
  if (blocked.length === 0) {
    return <p className="blocked-list__empty">You haven&apos;t blocked anyone.</p>;
  }

  return (
    <>
      <ul className="blocked-list">
        {blocked.map((b) => (
          <li className="blocked-list__row" key={b.profileId}>
            <span className="blocked-list__name">{b.displayName}</span>
            <button
              className="blocked-list__btn"
              onClick={() => setConfirming(b)}
              disabled={confirming?.profileId === b.profileId}
            >
              Unblock
            </button>
          </li>
        ))}
      </ul>

      {confirming && (
        <div className="blocked-list__confirm">
          <UnblockConfirm
            profileId={confirming.profileId}
            displayName={confirming.displayName}
            onCancel={() => setConfirming(null)}
            onDone={() => {
              setConfirming(null);
              void load();
            }}
          />
        </div>
      )}
    </>
  );
}

export default BlockedList;
